import type { Chapter } from './chapters';
import type { Camera } from './footage';
import { STAGE } from './stage-style';
import { frameIndexAt, type Telemetry } from './telemetry';

export const EXPORT_FPS = 30;
export const EXPORT_TIMESCALE = 90_000;
const REEL_LEAD_IN_MS = 8000;
const REEL_LEAD_OUT_MS = 4000;
const TILE_WIDTH = 960;
const TILE_CSS_WIDTH = 480;
const SINGLE_WIDTH = 1280;
const TILE_ASPECT = 3 / 4;
const MACROBLOCK = 16;
const TELEMETRY_STALE_S = 0.25;
const SECOND_MS = 1000;
const HIGH_PROFILE = '6400';

const AVC_LEVELS: readonly { readonly level: number; readonly maxFrame: number; readonly maxRate: number }[] = [
  { level: 31, maxFrame: 3600, maxRate: 108_000 },
  { level: 32, maxFrame: 5120, maxRate: 216_000 },
  { level: 40, maxFrame: 8192, maxRate: 245_760 },
  { level: 42, maxFrame: 8704, maxRate: 522_240 },
  { level: 50, maxFrame: 22_080, maxRate: 589_824 },
  { level: 51, maxFrame: 36_864, maxRate: 983_040 },
  { level: 52, maxFrame: 36_864, maxRate: 2_073_600 },
];

export type ExportLayout = 'quad' | 'hexa' | 'single';

export interface ExportClip {
  readonly file: Blob;
  readonly start: number;
  readonly span: number;
}

export interface ExportTrack {
  readonly camera: Camera;
  readonly clips: readonly ExportClip[];
}

export interface ExportTelemetry {
  readonly start: number;
  readonly telemetry: Telemetry;
}

export interface SegmentChapter {
  readonly time: number;
  readonly title: string;
}

export interface ExportSegment {
  readonly start: number;
  readonly end: number;
  readonly utcOffset: number;
  readonly tracks: readonly ExportTrack[];
  readonly telemetry: readonly ExportTelemetry[];
  readonly chapters: readonly SegmentChapter[];
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Canvas {
  readonly width: number;
  readonly height: number;
  readonly scale: number;
  readonly tiles: readonly Rect[];
}

interface Grid {
  readonly columns: number;
  readonly rows: number;
  readonly tileWidth: number;
  readonly tileCssWidth: number;
}

const GRIDS: Readonly<Record<ExportLayout, Grid>> = {
  quad: { columns: 2, rows: 2, tileWidth: TILE_WIDTH, tileCssWidth: TILE_CSS_WIDTH },
  hexa: { columns: 3, rows: 2, tileWidth: TILE_WIDTH, tileCssWidth: TILE_CSS_WIDTH },
  single: { columns: 1, rows: 1, tileWidth: SINGLE_WIDTH, tileCssWidth: 2 * TILE_CSS_WIDTH + STAGE.inset },
};

export interface MomentWindow {
  readonly eventId: string;
  readonly time: number;
  readonly title: string;
  readonly eventStart: number;
  readonly eventEnd: number;
}

export interface ReelWindow {
  readonly eventId: string;
  readonly start: number;
  readonly end: number;
  readonly chapters: readonly SegmentChapter[];
}

export const frameCount = (segment: Pick<ExportSegment, 'start' | 'end'>, fps = EXPORT_FPS): number =>
  Math.max(1, Math.round(((segment.end - segment.start) / SECOND_MS) * fps));

export const frameTime = (segment: Pick<ExportSegment, 'start'>, frame: number, fps = EXPORT_FPS): number =>
  segment.start + (frame * SECOND_MS) / fps;

export function outputChapters(segments: readonly ExportSegment[], fps = EXPORT_FPS): readonly Chapter[] {
  let offset = 0;
  return segments.flatMap((segment) => {
    const chapters = segment.chapters.map((chapter) => ({
      start: (offset + Math.round(((chapter.time - segment.start) / SECOND_MS) * fps)) / fps,
      title: chapter.title,
    }));
    offset += frameCount(segment, fps);
    return chapters;
  });
}

export function clipAt(track: ExportTrack, time: number): { readonly clip: ExportClip; readonly mediaTime: number } | undefined {
  const clip = track.clips.find((candidate) => time >= candidate.start && time <= candidate.start + candidate.span * SECOND_MS);
  return clip && { clip, mediaTime: (time - clip.start) / SECOND_MS };
}

export function telemetryAt(entries: readonly ExportTelemetry[], time: number): { readonly telemetry: Telemetry; readonly index: number } | undefined {
  for (const entry of entries) {
    const mediaTime = (time - entry.start) / SECOND_MS;
    const index = frameIndexAt(entry.telemetry.time, mediaTime);
    if (index >= 0 && mediaTime - entry.telemetry.time[index]! <= TELEMETRY_STALE_S) return { telemetry: entry.telemetry, index };
  }
  return undefined;
}

export function canvasFor(layout: ExportLayout): Canvas {
  const { columns, rows, tileWidth, tileCssWidth } = GRIDS[layout];
  const scale = tileWidth / tileCssWidth;
  const gutter = Math.round(STAGE.inset * scale);
  const tileHeight = tileWidth * TILE_ASPECT;
  const tiles = Array.from({ length: columns * rows }, (_, i) => ({
    x: gutter + (i % columns) * (tileWidth + gutter),
    y: gutter + Math.floor(i / columns) * (tileHeight + gutter),
    width: tileWidth,
    height: tileHeight,
  }));
  return { width: columns * (tileWidth + gutter) + gutter, height: rows * (tileHeight + gutter) + gutter, scale, tiles };
}

export function avcCodec(width: number, height: number, fps = EXPORT_FPS): string {
  const frame = Math.ceil(width / MACROBLOCK) * Math.ceil(height / MACROBLOCK);
  const level = AVC_LEVELS.find((candidate) => frame <= candidate.maxFrame && frame * fps <= candidate.maxRate) ?? AVC_LEVELS.at(-1)!;
  return `avc1.${HIGH_PROFILE}${level.level.toString(16).padStart(2, '0')}`;
}

export function reelWindows(moments: readonly MomentWindow[]): readonly ReelWindow[] {
  const windows: { eventId: string; start: number; end: number; chapters: SegmentChapter[] }[] = [];
  for (const moment of [...moments].sort((a, b) => a.eventId.localeCompare(b.eventId) || a.time - b.time)) {
    const start = Math.max(moment.eventStart, moment.time - REEL_LEAD_IN_MS);
    const end = Math.min(moment.eventEnd, moment.time + REEL_LEAD_OUT_MS);
    const last = windows.at(-1);
    if (last && last.eventId === moment.eventId && start <= last.end) {
      last.end = Math.max(last.end, end);
      last.chapters.push({ time: start, title: moment.title });
      continue;
    }
    windows.push({ eventId: moment.eventId, start, end, chapters: [{ time: start, title: moment.title }] });
  }
  return windows.sort((a, b) => a.start - b.start);
}
